import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WinnerAssetExportButton from '../components/WinnerAssetExportButton';

const FILTERS = ['All', 'CIG', 'ILG', 'Test'];

export default function WinnerAssetsOverview() {
    const { year } = useParams();
    const navigate = useNavigate();
    const selectedYear = Number(year);
    const [years, setYears] = React.useState([]);
    const [data, setData] = React.useState(null);
    const [filter, setFilter] = React.useState('All');
    const [search, setSearch] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    const load = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [configResponse, overviewResponse] = await Promise.all([
                api.get('/admin/api/winner-assets/config'),
                api.get(`/admin/api/winner-assets/${selectedYear}/overview`),
            ]);
            setYears(configResponse.data?.years || []);
            setData(overviewResponse.data || null);
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Unable to load winner assets.');
        } finally {
            setLoading(false);
        }
    }, [selectedYear]);

    React.useEffect(() => { load(); }, [load]);

    const rows = React.useMemo(() => {
        const query = search.trim().toLowerCase();
        return (data?.schools || []).filter((school) => {
            const matchesSearch = !query || String(school.schoolName || '').toLowerCase().includes(query);
            if (!matchesSearch || filter === 'All') return matchesSearch;
            if (filter === 'Test') return school.testSchool;
            return Array.isArray(school.winnerTypes) && school.winnerTypes.includes(filter);
        });
    }, [data, filter, search]);

    return (
        <Box sx={{ width: '92%', maxWidth: '100%', minWidth: 0, mx: 'auto', py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1">Winner Assets</Typography>
                    <Typography color="text.secondary">Review files submitted by winning schools.</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel id="winner-assets-year-label">Program year</InputLabel>
                        <Select
                            labelId="winner-assets-year-label"
                            label="Program year"
                            value={years.includes(selectedYear) ? selectedYear : ''}
                            onChange={(event) => navigate(`/winner-assets/${event.target.value}`)}
                        >
                            {years.map((configuredYear) => <MenuItem key={configuredYear} value={configuredYear}>{configuredYear}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Tabs value={filter} onChange={(_, value) => setFilter(value)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                {FILTERS.map((item) => <Tab key={item} value={item} label={item} />)}
            </Tabs>
            <TextField
                size="small"
                placeholder="Search school name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 320 }, mb: 2 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />

            {loading && !data ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
                <TableContainer
                    aria-label="Winner assets table"
                    tabIndex={0}
                    sx={{ border: '1px solid', borderColor: 'divider', width: '100%', maxWidth: '100%', overflowX: 'auto' }}
                >
                    <Table sx={{ minWidth: 760 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Winner Type</TableCell>
                                {(data?.cycle?.categories || []).map((category) => (
                                    <TableCell key={category.key} align="center" sx={{ fontWeight: 700 }}>{category.label}</TableCell>
                                ))}
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((school) => (
                                <TableRow key={school.schoolId} hover>
                                    <TableCell>{school.schoolName}</TableCell>
                                    <TableCell><WinnerTypeChip label={school.winnerTypeLabel} /></TableCell>
                                    {(data?.cycle?.categories || []).map((category) => (
                                        <TableCell key={category.key} align="center">
                                            {school.counts?.[category.key] || 0} / {category.maxFiles}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                                            <Button size="small" variant="outlined" onClick={() => navigate(`/winner-assets/${selectedYear}/schools/${school.schoolId}`)}>
                                                View Assets
                                            </Button>
                                            <WinnerAssetExportButton year={selectedYear} schoolId={school.schoolId} label="Download All" />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && <TableRow><TableCell colSpan={3 + (data?.cycle?.categories?.length || 0)} align="center">No schools found.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export function WinnerTypeChip({ label }) {
    const colors = label === 'TEST' ? { bgcolor: '#616161', color: '#fff' }
        : label === 'CIG + ILG' ? { bgcolor: '#222', color: '#fff' }
            : { bgcolor: '#F96302', color: '#fff' };
    return <Chip label={label} size="small" sx={{ ...colors, fontWeight: 700 }} />;
}